import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useConnectionInfo } from '@api/ai-access/ai-access'
import type { MCPConnectionInfoDTO } from '@api/fastAPI.schemas'
import { useSnackbar } from '@ui/snackbar'

const CopyValue = ({ label, value }: { label: string; value: string }): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      enqueueSnackbar(`${label} kopiert`, { variant: 'success' })
    } catch {
      // Clipboard access can be refused (permissions, insecure context).
      enqueueSnackbar(`${label} konnte nicht kopiert werden`, { variant: 'error' })
    }
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ fontFamily: 'monospace', minWidth: 0, flex: 1 }}
        noWrap
        title={value}
      >
        {value}
      </Typography>
      <Tooltip title={`${label} kopieren`}>
        <IconButton size="small" onClick={handleCopy} data-cy="ai-access-copy">
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

const McpConnectionInfo = (): ReactElement | null => {
  const { enqueueSnackbar } = useSnackbar()
  const { data } = useConnectionInfo<MCPConnectionInfoDTO, AxiosError>({
    query: {
      onError: error => {
        enqueueSnackbar(
          buildErrorMessage(error, 'MCP-Verbindungsinfo konnte nicht geladen werden'),
          { variant: 'error' },
        )
      },
    },
  })

  if (!data) return null

  return (
    <Box data-cy="ai-access-connection-info">
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        MCP-Verbindung
      </Typography>
      <Stack spacing={0.5}>
        <CopyValue label="Server-URL" value={data.server_url} />
        {data.oauth_client_id && <CopyValue label="OAuth Client-ID" value={data.oauth_client_id} />}
        <Typography variant="caption" color="text.secondary">
          Client Secret und Client ID für die Verbindung mit dem MCP-Server bitte beim Support
          anfragen.
        </Typography>
      </Stack>
    </Box>
  )
}

export default McpConnectionInfo
